export const DEFAULT_RECLAIM_REVENUE_SHARE = 0.25;
export const DEFAULT_RECLAIM_DEFLECTION_PRICE_PER_EVENT_USD = 15;
export const DEFAULT_RECLAIM_DEFLECTION_CHARGEBACK_PREVENTION_RATE = 0.4;
export const DEFAULT_RECLAIM_DEFLECTION_BILLABLE_EVENT_MULTIPLIER = 1.5;
export const DEFAULT_RECLAIM_ALERT_VISA_PRICE_PER_CASE_USD = 15;
export const DEFAULT_RECLAIM_ALERT_MASTERCARD_PRICE_PER_CASE_USD = 25;
export const DEFAULT_RECLAIM_ALERT_VISA_SHARE = 0.7;
export const DEFAULT_RECLAIM_ALERT_PRICE_PER_CASE_USD =
  computeReclaimBlendedAlertPricePerCaseUsd({
    mastercardPricePerCaseUsd:
      DEFAULT_RECLAIM_ALERT_MASTERCARD_PRICE_PER_CASE_USD,
    visaPricePerCaseUsd: DEFAULT_RECLAIM_ALERT_VISA_PRICE_PER_CASE_USD,
    visaShare: DEFAULT_RECLAIM_ALERT_VISA_SHARE,
  });

export interface ReclaimOpportunityWaterfallAssumptions {
  includeRepresentment: boolean;
  includeDeflection: boolean;
  includeAlerts: boolean;
  representmentRevenueShare: number;
  representmentWinRate: number;
  representmentEligibleRate: number;
  deflectionChargebackPreventionRate: number;
  deflectionBillableEventMultiplier: number;
  deflectionPricePerEventUsd: number;
  alertCoverageRate: number;
  alertPercentBelowThreshold: number;
  alertRefundRate: number;
  alertPreventedChargebackRate: number;
  alertPricePerCaseUsd: number;
  autoRefundThresholdUsd: number;
}

export interface ReclaimOpportunityWaterfallBaseline {
  chargebackCount: number;
  chargebackVolumeUsd: number;
  preDisputeCount: number;
  preDisputeVolumeUsd: number;
}

export interface ReclaimOpportunityWaterfallEstimate {
  baselineChargebackCount: number;
  baselineChargebackVolumeUsd: number;
  preDisputeCount: number;
  preDisputeVolumeUsd: number;
  deflectionPreventedChargebackCount: number;
  billableDeflectionEventCount: number;
  successfulDeflectionCount: number;
  deflectedVolumeUsd: number;
  deflectionRevenueUsd: number;
  alertCaseCount: number;
  autoRefundedAlertCount: number;
  alertRefundedVolumeUsd: number;
  alertPreventedChargebackVolumeUsd: number;
  alertRevenueUsd: number;
  residualChargebackVolumeUsd: number;
  residualEligibleChargebackVolumeUsd: number;
  representmentRevenueUsd: number;
  totalRevenueUsd: number;
}

export interface ReclaimOpportunityAdditiveComparisonEstimate {
  representmentRevenueUsd: number;
  totalRevenueUsd: number;
  overstatementUsd: number;
}

function clampRate(rate: number): number {
  if (!Number.isFinite(rate)) {
    return 0;
  }
  return Math.min(Math.max(rate, 0), 1);
}

function clampNonNegative(value: number): number {
  if (!Number.isFinite(value)) {
    return 0;
  }
  return Math.max(value, 0);
}

function averageAmount(volumeUsd: number, count: number): number {
  return count <= 0 ? 0 : volumeUsd / count;
}

export function computeReclaimBlendedAlertPricePerCaseUsd({
  mastercardPricePerCaseUsd,
  visaPricePerCaseUsd,
  visaShare,
}: {
  mastercardPricePerCaseUsd: number;
  visaPricePerCaseUsd: number;
  visaShare: number;
}): number {
  const normalizedVisaShare = clampRate(visaShare);
  return (
    clampNonNegative(visaPricePerCaseUsd) * normalizedVisaShare +
    clampNonNegative(mastercardPricePerCaseUsd) * (1 - normalizedVisaShare)
  );
}

export function computeReclaimRepresentativeDealSize({
  chargebackVolumeUsd,
  revenueShare,
  winRate,
}: {
  chargebackVolumeUsd: number;
  revenueShare: number;
  winRate: number;
}): number {
  return chargebackVolumeUsd * revenueShare * winRate;
}

export function computeReclaimOpportunityWaterfallEstimate({
  assumptions,
  baseline,
}: {
  assumptions: ReclaimOpportunityWaterfallAssumptions;
  baseline: ReclaimOpportunityWaterfallBaseline;
}): ReclaimOpportunityWaterfallEstimate {
  const chargebackCount = clampNonNegative(baseline.chargebackCount);
  const chargebackVolumeUsd = clampNonNegative(baseline.chargebackVolumeUsd);
  const preDisputeCount = clampNonNegative(baseline.preDisputeCount);
  const preDisputeVolumeUsd = clampNonNegative(baseline.preDisputeVolumeUsd);

  const deflectionChargebackPreventionRate = assumptions.includeDeflection
    ? clampRate(assumptions.deflectionChargebackPreventionRate)
    : 0;
  const deflectionPreventedChargebackCount =
    chargebackCount * deflectionChargebackPreventionRate;
  const billableDeflectionEventCount = Math.min(
    preDisputeCount || deflectionPreventedChargebackCount,
    deflectionPreventedChargebackCount *
      clampNonNegative(assumptions.deflectionBillableEventMultiplier),
  );
  const successfulDeflectionCount = deflectionPreventedChargebackCount;
  const deflectedVolumeUsd =
    chargebackVolumeUsd * deflectionChargebackPreventionRate;
  const deflectionRevenueUsd =
    billableDeflectionEventCount *
    clampNonNegative(assumptions.deflectionPricePerEventUsd);

  const remainingAfterDeflectionCount = Math.max(
    chargebackCount - successfulDeflectionCount,
    0,
  );
  const remainingAfterDeflectionVolumeUsd = Math.max(
    chargebackVolumeUsd - deflectedVolumeUsd,
    0,
  );
  const alertCaseCount =
    remainingAfterDeflectionCount *
    (assumptions.includeAlerts ? clampRate(assumptions.alertCoverageRate) : 0);
  const autoRefundedAlertCount =
    alertCaseCount *
    clampRate(assumptions.alertPercentBelowThreshold) *
    clampRate(assumptions.alertRefundRate);
  const remainingAverageUsd = averageAmount(
    remainingAfterDeflectionVolumeUsd,
    remainingAfterDeflectionCount,
  );
  const thresholdUsd = clampNonNegative(assumptions.autoRefundThresholdUsd);
  const averageRefundedAlertUsd =
    thresholdUsd === 0
      ? remainingAverageUsd
      : Math.min(remainingAverageUsd, thresholdUsd);
  const alertRefundedVolumeUsd =
    autoRefundedAlertCount * averageRefundedAlertUsd;
  const alertPreventedChargebackVolumeUsd =
    alertRefundedVolumeUsd *
    clampRate(assumptions.alertPreventedChargebackRate);
  const alertRevenueUsd =
    alertCaseCount * clampNonNegative(assumptions.alertPricePerCaseUsd);

  const residualChargebackVolumeUsd = Math.max(
    remainingAfterDeflectionVolumeUsd - alertPreventedChargebackVolumeUsd,
    0,
  );
  const residualEligibleChargebackVolumeUsd =
    residualChargebackVolumeUsd *
    clampRate(assumptions.representmentEligibleRate);
  const representmentRevenueUsd = assumptions.includeRepresentment
    ? computeReclaimRepresentativeDealSize({
        chargebackVolumeUsd: residualEligibleChargebackVolumeUsd,
        revenueShare: clampRate(assumptions.representmentRevenueShare),
        winRate: clampRate(assumptions.representmentWinRate),
      })
    : 0;

  return {
    baselineChargebackCount: chargebackCount,
    baselineChargebackVolumeUsd: chargebackVolumeUsd,
    preDisputeCount,
    preDisputeVolumeUsd,
    deflectionPreventedChargebackCount,
    billableDeflectionEventCount,
    successfulDeflectionCount,
    deflectedVolumeUsd,
    deflectionRevenueUsd,
    alertCaseCount,
    autoRefundedAlertCount,
    alertRefundedVolumeUsd,
    alertPreventedChargebackVolumeUsd,
    alertRevenueUsd,
    residualChargebackVolumeUsd,
    residualEligibleChargebackVolumeUsd,
    representmentRevenueUsd,
    totalRevenueUsd:
      deflectionRevenueUsd + alertRevenueUsd + representmentRevenueUsd,
  };
}

export function computeReclaimOpportunityAdditiveComparisonEstimate({
  assumptions,
  waterfallEstimate,
}: {
  assumptions: ReclaimOpportunityWaterfallAssumptions;
  waterfallEstimate: ReclaimOpportunityWaterfallEstimate;
}): ReclaimOpportunityAdditiveComparisonEstimate {
  const representmentRevenueUsd = computeReclaimRepresentativeDealSize({
    chargebackVolumeUsd: assumptions.includeRepresentment
      ? waterfallEstimate.baselineChargebackVolumeUsd *
        clampRate(assumptions.representmentEligibleRate)
      : 0,
    revenueShare: clampRate(assumptions.representmentRevenueShare),
    winRate: clampRate(assumptions.representmentWinRate),
  });
  const totalRevenueUsd =
    (assumptions.includeDeflection
      ? waterfallEstimate.deflectionRevenueUsd
      : 0) +
    (assumptions.includeAlerts ? waterfallEstimate.alertRevenueUsd : 0) +
    representmentRevenueUsd;

  return {
    representmentRevenueUsd,
    totalRevenueUsd,
    overstatementUsd: Math.max(
      totalRevenueUsd - waterfallEstimate.totalRevenueUsd,
      0,
    ),
  };
}
